import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Component } from '@angular/core';
import {
  DataStateChangeEvent,
  GridDataResult,
  FilterService,
} from '@progress/kendo-angular-grid';
import {
  ColumnOptions,
  GridState,
  GridStateCompositeFilterDescriptor,
  LoaderService,
  MessageService,
  NamedEntity,
} from '@shared';
import { ChangesetService } from '@shared-data';
import { TokenService } from '@auth';
import { GridRowChangesetException } from '../../models';

@Component({
  selector: 'app-sync-exceptions',
  templateUrl: './sync-exceptions.component.html',
  styleUrl: './sync-exceptions.component.scss',
})
export class SyncExceptionsComponent {
  changesets: GridDataResult = { data: [], total: 0 };

  pageSize = 10;
  state = new GridState(this.pageSize);

  columns: ColumnOptions[] = [];

  fontSize: number = 0.875;

  types: NamedEntity[] = [
    new NamedEntity('pricing', 'Pricing'),
    new NamedEntity('availability', 'Availability'),
  ];
  selectedType: NamedEntity = this.types[0];
  isAvailabilityOnly: boolean = false;

  get customFilterView(): string {
    return 'sync-exceptions';
  }

  filterCache: Map<string, string[]> = new Map<string, string[]>();

  onCustomFilterChange(
    column: string,
    event: string[],
    filterService: FilterService,
  ) {
    this.filterCache.set(column, event);

    filterService.filter({
      filters: event.map((value) => ({
        field: column,
        operator: 'eq',
        value,
      })),
      logic: 'or',
    });
  }

  filterChanged(event: CompositeFilterDescriptor) {
    if (event.filters.length === 0) {
      this.filterCache.clear();
    }

    for (const field of this.filterCache.keys()) {
      if (!GridState.hasField(event, field)) {
        this.filterCache.delete(field);
      }
    }
  }

  constructor(
    private changesetService: ChangesetService,
    private messageService: MessageService,
    private loaderService: LoaderService,
    private tokenService: TokenService,
  ) {
    this.columns = new GridRowChangesetException().getColumns();
  }

  async ngOnInit() {
    this.isAvailabilityOnly = await this.tokenService.isAvailabilityOnly();

    // For AvailabilityOnly users, filter types to only show Availability
    if (this.isAvailabilityOnly) {
      this.types = this.types.filter((type) => type.id === 'availability');
      this.selectedType = this.types[0];
    }

    this.setDefaultState();
  }

  onDataStateChanged(event: DataStateChangeEvent) {
    this.fetchChangesets(event as GridState);
  }

  setDefaultState() {
    this.filterCache.clear();
    this.state = new GridState(GridState.getPageSizeByFontSize(this.fontSize));

    this.fetchChangesets(this.state);
  }

  fetchChangesets(state: GridState) {
    this.loaderService.show();
    if (state.filter !== undefined) {
      this.state.filter = state.filter;
    }
    if (state.skip !== undefined) {
      this.state.skip = state.skip;
    }
    if (state.take !== undefined) {
      this.state.take = state.take;
    }
    if (state.sort?.length) {
      this.state.sort = state.sort;
    } else {
      this.state.sort = [{ field: 'scheduledDate', dir: 'desc' }];
    }

    this.state.filter = new GridStateCompositeFilterDescriptor(
      this.state.filter,
    )?.and({
      field: 'type',
      operator: 'eq',
      value: this.selectedType.id,
    });

    this.changesetService.getChangesetExceptions(this.state).subscribe({
      next: (changesets) => {
        this.toggleColumnVisibility();
        this.changesets = changesets;
        this.loaderService.hide();
      },
      error: () => {
        this.messageService.error('Failed to load changesets');
        this.loaderService.hide();
      },
    });
  }

  toggleColumnVisibility() {
    const pricingColumns = this.columns.filter(
      (column) =>
        column.field == 'newPrice' ||
        column.field == 'newDeliveryPrice' ||
        column.field == 'currentPrice' ||
        column.field == 'currentDeliveryPrice',
    );
    pricingColumns.forEach(
      (column) => (column.hidden = this.selectedType.id != 'pricing'),
    );

    const availabilityColumns = this.columns.filter(
      (column) =>
        column.field == 'newAvailability' ||
        column.field == 'currentAvailability' ||
        column.field =='newDeliveryAvailability3P' || 
        column.field == 'deliveryAvailability3P' ||
        column.field == 'deliveryAvailability1P' ||
        column.field == 'newDeliveryAvailability1P',
    );
    availabilityColumns.forEach(
      (column) => (column.hidden = this.selectedType.id != 'availability'),
    );
  }
}
